import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Copy, Check } from "lucide-react";
import { TREASURY, usd } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/gtax`;

export const DepositPanel = ({ record, address, compact }) => {
  const [net, setNet] = useState(TREASURY.networks[0].id);
  const [copied, setCopied] = useState(false);
  const [txid, setTxid] = useState("");
  const [done, setDone] = useState(false);
  const network = TREASURY.networks.find((n) => n.id === net);
  const copy = async () => {
    await navigator.clipboard.writeText(address).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const notify = async () => {
    try {
      await axios.post(`${API}/deposits`, { reference: record.reference, email: record.email, txid });
      setDone(true);
    } catch (err) {
      toast.error("Please paste the full transaction hash.");
    }
  };
  return (
    <div className="space-y-10" data-testid="gtax-deposit-panel">
      <div className="flex flex-wrap justify-between gap-6 pb-8 border-b border-[color:var(--line)]">
        <div>
          <div className="gx-eyebrow">Settlement</div>
          {!compact && <div className="gx-serif text-3xl mt-2" data-testid="gtax-deposit-reference">{record.reference}</div>}
          <div className="text-sm text-[color:var(--mute)] mt-1">Agreement signed {new Date(record.accepted_at).toLocaleString()} · Identity verified</div>
        </div>
        <div className="text-right">
          <div className="gx-eyebrow">Allocation</div>
          <div className="gx-serif text-3xl mt-2 gx-gold">{usd(record.amount)}</div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="gtax-deposit-networks">
        {TREASURY.networks.map((n) => (
          <button key={n.id} onClick={() => setNet(n.id)} className={`gx-ghost !px-5 !py-2 ${net === n.id ? "!border-[color:var(--gold)] !text-[color:var(--gold2)]" : ""}`} data-testid={`gtax-deposit-network-${n.id}`}>
            {n.label}{n.recommended ? " · lower fees" : ""}
          </button>
        ))}
      </div>
      <div className="grid md:grid-cols-[240px_1fr] gap-10 items-center">
        <div className="bg-white p-4 rounded" data-testid="gtax-deposit-qr"><QRCodeSVG value={address} size={208} fgColor="#1E1A14" /></div>
        <div>
          <div className="gx-eyebrow">GTA X Consolidated Treasury · {network.label}</div>
          <div className="mt-3 font-mono text-base md:text-lg break-all" data-testid="gtax-deposit-address">{address}</div>
          <button onClick={copy} className="gx-ghost mt-5 inline-flex items-center gap-2" data-testid="gtax-deposit-copy">
            {copied ? <Check size={16} /> : <Copy size={16} />}{copied ? "Copied" : "Copy address"}
          </button>
          <ul className="mt-6 space-y-2 text-sm text-[color:var(--mute)]">
            <li>Accepted: {network.assets} on {network.label} only.</li>
            <li>Sending on any other network will result in permanent loss of funds.</li>
            <li>Include reference <b className="text-[color:var(--ink)]">{record.reference}</b> when you confirm below.</li>
          </ul>
        </div>
      </div>
      <div className="pt-8 border-t border-[color:var(--line)]">
        {done ? (
          <p className="gx-serif text-2xl" data-testid="gtax-deposit-done">Thank you. Your settlement is being verified on-chain and will be credited to your member ledger on confirmation.</p>
        ) : (
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[260px]">
              <label className="block text-xs tracking-widest uppercase text-[color:var(--mute)]">Transaction hash (after sending)</label>
              <input value={txid} onChange={(e) => setTxid(e.target.value)} className="w-full py-3 font-mono" data-testid="gtax-deposit-txid" />
            </div>
            <button disabled={txid.trim().length < 10} onClick={notify} className="gx-btn disabled:opacity-40" data-testid="gtax-deposit-submit">Confirm settlement</button>
          </div>
        )}
      </div>
    </div>
  );
};
